import { useState, useEffect, useCallback, useRef } from "react";

// Session duration: 1 hour = 60 minutes
const SESSION_DURATION_MINUTES = 60;

// Warning intervals in minutes before logout
const WARNING_INTERVALS = [15, 10, 5];

/**
 * Custom hook to manage session timeout warnings
 * Shows warnings at 15, 10, and 5 minutes before session expires
 * 
 * @returns {Object} - { showWarning, minutesRemaining, dismissWarning, resetSession }
 */
export const useSessionTimeout = () => {
  const [showWarning, setShowWarning] = useState(false);
  const [minutesRemaining, setMinutesRemaining] = useState(null);
  const [sessionStartTime, setSessionStartTime] = useState(Date.now());
  const [dismissedWarnings, setDismissedWarnings] = useState([]);
  
  const timerRef = useRef(null);

  // Reset session timer (call this on user activity if needed)
  const resetSession = useCallback(() => {
    setSessionStartTime(Date.now());
    setDismissedWarnings([]);
    setShowWarning(false);
    setMinutesRemaining(null);
  }, []);

  // Dismiss the current warning
  const dismissWarning = useCallback(() => {
    if (minutesRemaining !== null) {
      // Find which warning interval this corresponds to
      const warningInterval = WARNING_INTERVALS.find(
        (interval) => minutesRemaining <= interval && minutesRemaining > (WARNING_INTERVALS[WARNING_INTERVALS.indexOf(interval) + 1] || 0)
      );
      
      if (warningInterval) {
        setDismissedWarnings((prev) => [...prev, warningInterval]);
      }
    }
    setShowWarning(false);
  }, [minutesRemaining]);

  useEffect(() => {
    const checkSessionTime = () => {
      const elapsedMs = Date.now() - sessionStartTime;
      const elapsedMinutes = elapsedMs / (1000 * 60);
      const remaining = SESSION_DURATION_MINUTES - elapsedMinutes;

      // Check if we should show a warning
      for (const warningInterval of WARNING_INTERVALS) {
        // Show warning when we cross into a warning interval
        // and haven't dismissed this warning yet
        if (
          remaining <= warningInterval &&
          remaining > (WARNING_INTERVALS[WARNING_INTERVALS.indexOf(warningInterval) + 1] || 0) &&
          !dismissedWarnings.includes(warningInterval)
        ) {
          setMinutesRemaining(Math.ceil(remaining));
          setShowWarning(true);
          break;
        }
      }

      // Update minutes remaining for display
      if (remaining > 0) {
        setMinutesRemaining(Math.ceil(remaining));
      }
    };

    // Check every 30 seconds
    timerRef.current = setInterval(checkSessionTime, 30000);

    // Initial check
    checkSessionTime();

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [sessionStartTime, dismissedWarnings]);

  return {
    showWarning,
    minutesRemaining,
    dismissWarning,
    resetSession,
  };
};

export default useSessionTimeout;
